/**
 * Where the inspection screens live for the surface the user is standing on.
 *
 * WHY THIS FUNCTION EXISTS AT ALL
 * -------------------------------
 * `AppRoutes` returns exactly ONE of several mutually exclusive route trees,
 * chosen by the auth flags. A path is registered per-tree, so a hardcoded
 * destination is only correct for whichever tree the author had in mind — and
 * in the other trees it does not 404, it falls through to that tree's
 * catch-all and silently redirects somewhere else.
 *
 * That is the bug this shipped to fix, verbatim. `EquipmentInspectionForm`
 * ended a successful submit with
 *
 *     navigate('/safety/records?tab=inspections')
 *
 * and `/safety/records` is registered ONLY in the AppLayout (office) tree. A
 * casing hand — TNT's whole crew — saw the "Inspection submitted" toast and was
 * then bounced to `/field` by the catch-all, with no record and no error. The
 * inspection had saved correctly every time. Nothing failed, nothing logged,
 * and it reads on the phone as "the app lost my inspection".
 *
 * Both the form's post-submit hop and the detail page's back arrow resolve
 * through here, so the two cannot drift into disagreeing about where a
 * person's inspections are. Adding a third surface means adding it once.
 *
 * KEYED ON THE PATH, NOT ON THE AUTH FLAGS. `isFieldWorker` says which tree was
 * SELECTED; the pathname says which tree the user is actually inside. They
 * agree today, but the office tree is reachable by a field-flagged account in
 * exactly one place — a shared `/forms/*` destination — and the pathname is the
 * answer that stays right there.
 */

/** The casing field tree's own inspections list. */
export const FIELD_INSPECTIONS_LIST = '/field/safety/inspections';

/** The office records screen, on its Inspections tab. */
export const OFFICE_INSPECTIONS_LIST = '/safety/records?tab=inspections';

/** True when `pathname` sits inside the casing field tree. */
export function isFieldSurface(pathname: string): boolean {
  return pathname === '/field' || pathname.startsWith('/field/');
}

/**
 * The list of inspections to send someone back to from `pathname`.
 *
 * The office string carries its `?tab=` because that screen is six tabs behind
 * one route; the field one does not because that screen is only inspections.
 */
export function inspectionsListPath(pathname: string): string {
  return isFieldSurface(pathname) ? FIELD_INSPECTIONS_LIST : OFFICE_INSPECTIONS_LIST;
}

/** One submission, read-only, on whichever surface `pathname` is on. */
export function inspectionDetailPath(pathname: string, id: string): string {
  return isFieldSurface(pathname)
    ? `${FIELD_INSPECTIONS_LIST}/${id}`
    : `/safety/inspections/${id}`;
}

/**
 * Reopening a DRAFT for editing. Field surface only — the office has no
 * equivalent, and a submitted inspection is a record rather than a form, so
 * callers must check `status === 'draft'` before offering this.
 */
export function inspectionResumePath(id: string): string {
  return `${FIELD_INSPECTIONS_LIST}/${id}/edit`;
}
